import type {
  AnalyticsCommonQuery,
  AttentionResponse,
  BudgetAlertsResponse,
  SummaryResponse,
  CostResponse,
  RepoCostResponse,
  AgentsResponse,
  AgentDrillResponse,
  FailureMode,
  CostEfficiencyResponse,
  SpendGroupBy,
  SpendResponse,
  InvoiceResponse,
  SpendForecast,
} from '../../../../shared/types/analytics';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

function qs(q: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v) params.set(k, v);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function readErr(r: Response, fallback: string): Promise<string> {
  try {
    const j = (await r.json()) as { error?: { message?: string } };
    if (j?.error?.message) return j.error.message;
  } catch {
    /* */
  }
  return fallback;
}

/** Common account-scoping param added to every analytics request. */
export type AnalyticsAccountQuery = { account?: string };

export async function fetchAttention(
  q: AnalyticsAccountQuery = {},
): Promise<AttentionResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/attention${qs(q as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load attention items'));
  return ((await r.json()) as AttentionResponse).data;
}

export async function fetchBudgetAlerts(
  q: AnalyticsAccountQuery = {},
): Promise<BudgetAlertsResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/budget-alerts${qs(q as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load budget alerts'));
  return ((await r.json()) as BudgetAlertsResponse).data;
}

export async function fetchSummary(
  q: AnalyticsCommonQuery & AnalyticsAccountQuery = {},
): Promise<SummaryResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/summary${qs(q as unknown as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load summary'));
  return ((await r.json()) as SummaryResponse).data;
}

export async function fetchCostBreakdown(
  q: AnalyticsCommonQuery & AnalyticsAccountQuery = {},
): Promise<CostResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/cost${qs(q as unknown as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load cost breakdown'));
  return ((await r.json()) as CostResponse).data;
}

export async function fetchRepoCost(
  q: AnalyticsCommonQuery & AnalyticsAccountQuery & { project: string },
): Promise<RepoCostResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/cost/repos${qs(q as unknown as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load repo cost'));
  return ((await r.json()) as RepoCostResponse).data;
}

export async function fetchAgents(
  q: AnalyticsCommonQuery & AnalyticsAccountQuery = {},
): Promise<AgentsResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/agents${qs(q as unknown as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load agents'));
  return ((await r.json()) as AgentsResponse).data;
}

export async function fetchAgentDrill(
  q: AnalyticsCommonQuery & AnalyticsAccountQuery & { agent: string; mode: FailureMode },
): Promise<AgentDrillResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/agents/drill${qs(q as unknown as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load drill rows'));
  return ((await r.json()) as AgentDrillResponse).data;
}

/**
 * FORGE-055 cost efficiency. The endpoint takes an ISO `since` (not the
 * AnalyticsRange enum) plus optional project/repo/limit. We fetch the
 * full window once and let the page filter client-side (FR-8).
 */
export async function fetchCostEfficiency(
  q: { since?: string; project?: string; repo?: string; limit?: number; account?: string } = {},
): Promise<CostEfficiencyResponse['data']> {
  const r = await authFetch(
    `${API_BASE}/analytics/cost-efficiency${qs({
      since: q.since,
      project: q.project,
      repo: q.repo,
      limit: q.limit !== undefined ? String(q.limit) : undefined,
      account: q.account,
    })}`,
  );
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load cost efficiency'));
  return ((await r.json()) as CostEfficiencyResponse).data;
}

// ---------------------------------------------------------------------------
// FORGE-304 — CFO spend report
// ---------------------------------------------------------------------------

export type SpendQuery = AnalyticsCommonQuery &
  AnalyticsAccountQuery & {
    groupBy?: SpendGroupBy[];
  };

function spendQs(q: SpendQuery): Record<string, string | undefined> {
  return {
    since: q.since,
    from: q.from,
    to: q.to,
    period: q.period,
    groupBy: q.groupBy && q.groupBy.length > 0 ? q.groupBy.join(',') : undefined,
    project: q.project,
    org: q.org,
    repo: q.repo,
    account: q.account,
  };
}

export async function fetchSpend(q: SpendQuery = {}): Promise<SpendResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/spend${qs(spendQs(q))}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load spend report'));
  return ((await r.json()) as SpendResponse).data;
}

/**
 * FR-11 — CSV export. `/analytics/spend.csv` requires the auth header, so a
 * plain `<a href>` won't work; fetch as a blob and trigger the browser
 * download client-side (same pattern as transcriptDownload.ts).
 */
export async function downloadSpendCsv(q: SpendQuery = {}): Promise<void> {
  const r = await authFetch(`${API_BASE}/analytics/spend.csv${qs(spendQs(q))}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to export spend CSV'));
  const blob = await r.blob();
  const disposition = r.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename="([^"]+)"/.exec(disposition);
  const filename = filenameMatch?.[1] ?? 'forge-spend.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function fetchInvoice(
  q: AnalyticsAccountQuery & { month?: string } = {},
): Promise<InvoiceResponse['data']> {
  const r = await authFetch(`${API_BASE}/analytics/invoice${qs(q as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load invoice'));
  return ((await r.json()) as InvoiceResponse).data;
}

export async function fetchForecast(
  q: AnalyticsAccountQuery = {},
): Promise<SpendForecast> {
  const r = await authFetch(`${API_BASE}/analytics/forecast${qs(q as Record<string, string | undefined>)}`);
  if (!r.ok) throw new Error(await readErr(r, 'Failed to load forecast'));
  return ((await r.json()) as { data: SpendForecast }).data;
}
